import { useCallback, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Anchor, CheckCircle2, FileUp, Loader2, RefreshCcw, XCircle, Zap, Clock, Server } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { api } from "@/lib/api";
import { formatInt } from "@/lib/utils";
import { uploadApFile, type WsEvent } from "@/lib/ws";
import { toast } from "sonner";

// Live parse stats delivered via SSE while parser-rs runs.
type ParseStats = {
  rows: number;
  rateRPS: number;
  etaSec: number;
};

type UploadState =
  | { kind: "idle" }
  | { kind: "uploading"; bytes: number; rows: number; total: number; filename: string; parseStats?: ParseStats }
  | { kind: "done"; batchId: string; rows: number; elapsedMs: number }
  | { kind: "error"; message: string };

export function UploadPage() {
  const [state, setState] = useState<UploadState>({ kind: "idle" });
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const esRef = useRef<EventSource | null>(null);

  const { data: batches, refetch } = useQuery({
    queryKey: ["batches"],
    queryFn: api.batches,
    refetchInterval: (q) => (state.kind === "uploading" ? 2000 : q.state.data ? 15_000 : 5000),
  });

  const handleUpload = useCallback(async (file: File) => {
    const controller = new AbortController();
    abortRef.current = controller;
    setState({ kind: "uploading", bytes: 0, rows: 0, total: file.size, filename: file.name });

    try {
      const result = await uploadApFile({
        file,
        signal: controller.signal,

        onReady: (batchId: string) => {
          // Parser is starting — open SSE to receive live row counts.
          const es = new EventSource(`/api/batches/${batchId}/stream`);
          esRef.current = es;

          es.onmessage = (e) => {
            try {
              const ev = JSON.parse(e.data) as {
                rows: number;
                rate_rps: number;
                eta_sec: number;
                done?: boolean;
              };
              setState((prev) =>
                prev.kind === "uploading"
                  ? {
                      ...prev,
                      parseStats: {
                        rows: ev.rows,
                        rateRPS: ev.rate_rps ?? 0,
                        etaSec: ev.eta_sec ?? -1,
                      },
                    }
                  : prev,
              );
              if (ev.done) {
                es.close();
                esRef.current = null;
              }
            } catch {
              // Malformed SSE payload — ignore.
            }
          };

          es.onerror = () => {
            es.close();
            esRef.current = null;
          };
        },

        onEvent: (ev: WsEvent) => {
          if (ev.type === "progress") {
            setState((prev) =>
              prev.kind === "uploading"
                ? { ...prev, bytes: ev.bytes, rows: ev.rows }
                : prev,
            );
          } else if (ev.type === "parsed") {
            // WS is done — close SSE if still open.
            esRef.current?.close();
            esRef.current = null;
          }
        },
      });

      setState({ kind: "done", batchId: result.batchId, rows: result.rows, elapsedMs: result.elapsedMs });
      refetch();
    } catch (err) {
      esRef.current?.close();
      esRef.current = null;
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, [refetch]);

  const onPick = () => inputRef.current?.click();

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Upload TD SYNNEX feed</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Drop your <code className="font-mono">.ap</code> file. parser-rs will stream-parse and upsert to Postgres.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Anchor className="h-4 w-4" /> Drop zone
          </CardTitle>
          <CardDescription>Tilde-delimited .ap files (HDR + DTL). ~350 MB / 1M rows supported.</CardDescription>
        </CardHeader>
        <CardContent>
          <div
            onClick={state.kind === "uploading" ? undefined : onPick}
            onDragOver={(e) => e.preventDefault()}
            onDrop={state.kind === "uploading" ? undefined : onDrop}
            className="border-2 border-dashed border-border/60 rounded-lg p-10 text-center cursor-pointer hover:border-ember/60 transition-colors"
          >
            <input
              ref={inputRef}
              type="file"
              accept="*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUpload(file);
              }}
            />
            {state.kind === "idle" && (
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <FileUp className="h-10 w-10" />
                <div className="text-sm">Click to browse or drag a .ap file here</div>
              </div>
            )}
            {state.kind === "uploading" && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-center gap-2 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin text-ember" />
                  {state.filename}
                </div>
                <Progress
                  value={state.total ? (state.bytes / state.total) * 100 : 0}
                />
                <div className="text-xs text-muted-foreground font-mono">
                  {formatInt(state.bytes)} / {formatInt(state.total)} bytes
                </div>
                {state.parseStats ? (
                  <div className="text-xs text-muted-foreground font-mono flex flex-col gap-1">
                    <span>
                      {formatInt(state.parseStats.rows)} rows parsed
                      {state.parseStats.rateRPS > 0 && (
                        <> · {formatInt(Math.round(state.parseStats.rateRPS))} rows/s</>
                      )}
                    </span>
                    {state.parseStats.etaSec >= 0 && (
                      <span>ETA {state.parseStats.etaSec.toFixed(0)}s</span>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground font-mono">
                    waiting for parser…
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="self-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    esRef.current?.close();
                    esRef.current = null;
                    abortRef.current?.abort();
                  }}
                >
                  Abort
                </Button>
              </div>
            )}
            {state.kind === "done" && (
              <div className="flex flex-col items-center gap-2 text-emerald-400">
                <CheckCircle2 className="h-10 w-10" />
                <div className="text-sm">
                  Parsed {formatInt(state.rows)} rows in {(state.elapsedMs / 1000).toFixed(1)}s — batch{" "}
                  <code className="font-mono text-xs">{state.batchId}</code>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setState({ kind: "idle" });
                  }}
                >
                  Upload another
                </Button>
              </div>
            )}
            {state.kind === "error" && (
              <div className="flex flex-col items-center gap-2 text-destructive">
                <XCircle className="h-10 w-10" />
                <div className="text-sm">{state.message}</div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setState({ kind: "idle" });
                  }}
                >
                  Try again
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent batches</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="num text-right">Rows</TableHead>
                <TableHead className="num text-right">Size</TableHead>
                <TableHead className="num text-right">Duration</TableHead>
                <TableHead>Started</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {batches?.items?.length ? (
                batches.items.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell className="font-mono text-xs">{b.filename}</TableCell>
                    <TableCell>
                      <BatchStatus status={b.status} />
                    </TableCell>
                    <TableCell className="num text-right">
                      {b.rows_processed > 0 ? formatInt(b.rows_processed) : "—"}
                    </TableCell>
                    <TableCell className="num text-right text-xs text-muted-foreground">
                      {formatBytes(b.size_bytes)}
                    </TableCell>
                    <TableCell className="num text-right text-xs text-muted-foreground">
                      {b.completed_at ? formatDuration(b.started_at, b.completed_at) : "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {new Date(b.started_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No batches yet — ingest your first feed.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── TD SYNNEX SFTP Feed ── */}
      <TDSynnexFeedCard />
    </div>
  );
}

// ── TD SYNNEX automated SFTP feed ────────────────────────────────────────────

interface FeedStatus {
  running: boolean;
  started_at?: string;
  finished_at?: string;
  batch_id?: string;
  rows_upserted: number;
  error?: string;
  file?: string;
}

function TDSynnexFeedCard() {
  const { data: status, refetch, isLoading } = useQuery<FeedStatus>({
    queryKey: ["feed-status"],
    queryFn: () => fetch("/api/feed/status").then(r => r.json()),
    refetchInterval: (q) => q.state.data?.running ? 3000 : 30_000,
  });

  const trigger = useMutation({
    mutationFn: () => fetch("/api/feed/trigger", { method: "POST" }).then(r => {
      if (!r.ok) return r.json().then(d => { throw new Error(d.error ?? "Failed"); });
      return r.json();
    }),
    onSuccess: () => {
      toast.success("TD SYNNEX sync started");
      setTimeout(() => refetch(), 1500);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const configured = status && !(status.error?.includes("SFTP not configured") ?? false);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <Server className="h-4 w-4 text-violet-500" />
              TD SYNNEX SFTP Feed
            </CardTitle>
            <CardDescription className="text-xs mt-0.5">
              Automated 24-hour .ap file download · set <code className="font-mono">TDSYNNEX_SFTP_*</code> env vars to enable
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => refetch()} className="text-muted-foreground hover:text-foreground">
              <RefreshCcw className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => trigger.mutate()}
              disabled={trigger.isPending || status?.running}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-violet-500/10 text-violet-400 border border-violet-500/30 hover:bg-violet-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {trigger.isPending || status?.running
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Zap className="h-3.5 w-3.5" />}
              {status?.running ? "Syncing…" : "Run now"}
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-12 rounded shimmer" />
        ) : !status || (!status.started_at && !status.running) ? (
          <div className="text-xs text-muted-foreground py-2">
            No sync has run yet.
            {!configured && (
              <span className="ml-1">
                Set <code className="font-mono">TDSYNNEX_SFTP_HOST</code>, <code className="font-mono">TDSYNNEX_SFTP_USER</code>, and <code className="font-mono">TDSYNNEX_SFTP_PASSWORD</code> in your <code className="font-mono">.env.local</code> to enable auto-sync.
              </span>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div className="rounded-md bg-muted/40 p-2.5">
              <div className="text-muted-foreground mb-1">Status</div>
              <div className="font-medium">
                {status.running ? (
                  <span className="text-violet-400 flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> Running
                  </span>
                ) : status.error ? (
                  <span className="text-rose-400">Failed</span>
                ) : (
                  <span className="text-emerald-400">Done</span>
                )}
              </div>
            </div>
            <div className="rounded-md bg-muted/40 p-2.5">
              <div className="text-muted-foreground mb-1">File</div>
              <div className="font-mono truncate">{status.file || "—"}</div>
            </div>
            <div className="rounded-md bg-muted/40 p-2.5">
              <div className="text-muted-foreground mb-1">Rows upserted</div>
              <div className="font-semibold tabular-nums">{status.rows_upserted > 0 ? formatInt(status.rows_upserted) : "—"}</div>
            </div>
            <div className="rounded-md bg-muted/40 p-2.5">
              <div className="text-muted-foreground mb-1 flex items-center gap-1"><Clock className="h-3 w-3" /> Last run</div>
              <div className="tabular-nums">
                {status.finished_at
                  ? new Date(status.finished_at).toLocaleString()
                  : status.started_at
                  ? new Date(status.started_at).toLocaleString()
                  : "—"}
              </div>
            </div>
            {status.error && (
              <div className="col-span-full rounded-md bg-rose-500/10 border border-rose-500/20 px-3 py-2 text-rose-400 font-mono text-[11px]">
                {status.error}
              </div>
            )}
            {status.batch_id && !status.running && (
              <div className="col-span-full text-muted-foreground">
                Batch: <code className="font-mono">{status.batch_id}</code>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BatchStatus({ status }: { status: string }) {
  switch (status) {
    case "completed":
      return <Badge variant="success">completed</Badge>;
    case "parsing":
      return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">parsing</Badge>;
    case "uploading":
      return <Badge variant="warning">uploading</Badge>;
    case "failed":
      return <Badge variant="destructive">failed</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function formatBytes(bytes: number): string {
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1_024) return `${(bytes / 1_024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function formatDuration(startedAt: string, completedAt: string): string {
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 0) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
}
